/**
 * The project's page-label list (`pdfPageNumbers`, one printed label per physical page)
 * must stay the same length as the LIVE PDF. Every page operation trusts it.
 *
 * Found 7 Oct 2026 (1,437 cues sitting on pages the PDF does not have, 35 productions):
 *
 *   1. Opening a saved version loads the VERSION's PDF, and the load-time tidy-up then
 *      trimmed the LIVE label list to that PDF's page count. A 30-page version of a
 *      35-page script cut the live labels to 30. Reproduced on sandbox.
 *   2. The next insert/replace/delete sends the label count to the PDF helper as
 *      `totalPages`, and the helper rebuilds the PDF from pages 1..totalPages. Pages 31-35
 *      were silently dropped from the script, and the cues on them were left past the end.
 *
 * healPageLabels fixes (1). pageCountForPageOp guards (2): a page operation only runs when
 * the label list and the loaded PDF agree, so a mismatch can never cut pages off again.
 */

/**
 * What the editor may write to the LIVE label list after loading a PDF.
 * @param {Array|undefined|null} labels  the live project's pdfPageNumbers
 * @param {number} pdfPageCount  pages in the PDF that was just loaded
 * @param {{ viewingVersion?: boolean }} [opts]
 * @returns {Array|null}  the labels to write, or null to write nothing
 */
export function healPageLabels(labels, pdfPageCount, opts) {
    // The PDF on screen is a saved version's, not the live script's. Its page count
    // says nothing about the live labels.
    if (opts && opts.viewingVersion) {
        return null;
    }
    // A page change is running (14 Oct 2026): the PDF helper shortens or lengthens the
    // PDF before the labels change, so the two disagree on purpose until it finishes.
    // A repair written now lands on top of the finished labels (Josh's big show).
    if (opts && opts.pageChangeInFlight) {
        return null;
    }
    if (!Number.isInteger(pdfPageCount) || pdfPageCount < 1) {
        return null;
    }
    // Only a STORED empty list is built as 1..N. Missing (not loaded yet, mid re-bind)
    // says nothing about the labels, and building them then replaced every one.
    if (!Array.isArray(labels)) {
        return null;
    }
    const list = labels;
    if (list.length === 0) {
        return Array.from({ length: pdfPageCount }, (_, i) => ({ id: i, name: `${i + 1}` }));
    }
    if (list.length > pdfPageCount) {
        return list.slice(0, pdfPageCount);
    }
    // Shorter than the PDF: left alone on purpose. Which labels the missing pages get
    // is an open decision (Josh), and pageCountForPageOp keeps page operations off
    // these productions until it is made.
    return null;
}

/**
 * The page count a page operation may send to the PDF helper, or a refusal.
 * @param {{ labels: Array|undefined|null, loadedPageCount: number }} args
 * @returns {{ ok: true, total: number } | { ok: false, reason: string }}
 */
export function pageCountForPageOp({ labels, loadedPageCount }) {
    const labelCount = Array.isArray(labels) ? labels.length : 0;
    if (!Number.isInteger(loadedPageCount) || loadedPageCount < 1) {
        return { ok: false, reason: 'The script has not finished loading.' };
    }
    if (labelCount !== loadedPageCount) {
        return {
            ok: false,
            reason: `Page numbering lists ${labelCount} pages but the script has ${loadedPageCount}.`
        };
    }
    return { ok: true, total: loadedPageCount };
}

/** 'a' -> 'b', 'z' -> 'aa' (the suffix rule the page modals already use). */
export function nextAlphaSuffix(suffix) {
    const arr = suffix.split('');
    let i = arr.length - 1;
    while (i >= 0) {
        if (arr[i] !== 'z') {
            arr[i] = String.fromCodePoint(arr[i].codePointAt(0) + 1);
            return arr.join('');
        }
        arr[i] = 'a';
        i--;
    }
    return 'a' + arr.join('');
}

/**
 * The live label list after replacing pages `from`..`to` (1-based, inclusive) with a
 * file of `newCount` pages. Always returns exactly (labels.length - range + newCount)
 * labels, so the list stays the same length as the new PDF.
 *   - more pages: lettered labels after the range, e.g. 5a, 5b (the existing rule)
 *   - same count: unchanged
 *   - FEWER pages: the labels of the pages that went away are dropped. Before 7 Oct
 *     2026 nothing was dropped; the next load then trimmed the TAIL, so every page after
 *     the range showed the wrong printed number.
 */
export function labelsAfterReplace(labels, { from, to, newCount }) {
    const list = Array.isArray(labels) ? [...labels] : [];
    const start = Number(from);
    const end = Number(to);
    const count = Number(newCount);
    const range = (end - start) + 1;
    if (count > range) {
        const prefix = end > 0 && list[end - 1] ? list[end - 1].name : '1';
        let suffix = 'a';
        for (let i = 0; i < count - range; i++) {
            list.splice(end + i, 0, { id: end + 1, name: `${prefix}${suffix}` });
            suffix = nextAlphaSuffix(suffix);
        }
    } else if (count < range) {
        // Pages start..start+count-1 hold the replacement; start+count..end are gone.
        list.splice((start - 1) + count, range - count);
    }
    return list.map((item, index) => ({ ...item, id: index }));
}

/**
 * Self-repair on open (Josh, 7 Oct 2026): the labels for a LIVE script whose list is
 * SHORTER than its PDF, extended to the PDF's page count, or null to write nothing.
 * Existing labels are kept exactly; the new ones number on from the last label's
 * number (12 -> 13; 12a -> 13; no number at all -> the page's position).
 * Never in a version view, never shortens, never touches an empty list (healPageLabels
 * builds those), never moves a cue or a page.
 */
export function extendedPageLabels(labels, pdfPageCount, opts) {
    if (opts && (opts.viewingVersion || opts.pageChangeInFlight)) {
        return null;
    }
    if (!Number.isInteger(pdfPageCount) || pdfPageCount < 1) {
        return null;
    }
    const list = Array.isArray(labels) ? labels : [];
    if (list.length === 0 || list.length >= pdfPageCount) {
        return null;
    }
    const last = String(list[list.length - 1].name);
    const m = /^(\d+)/.exec(last);
    let next = m ? Number(m[1]) + 1 : list.length + 1;
    const out = list.map((item, index) => ({ ...item, id: index }));
    for (let i = list.length; i < pdfPageCount; i++) {
        out.push({ id: i, name: `${next}` });
        next++;
    }
    return out;
}

export default { healPageLabels, pageCountForPageOp, labelsAfterReplace, extendedPageLabels, nextAlphaSuffix };

/**
 * Same printed labels, page for page (ids are positions and are ignored).
 * @param {Array} a
 * @param {Array} b
 * @returns {boolean}
 */
export function sameLabelNames(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
        return false;
    }
    return a.every((item, i) => String(item && item.name) === String(b[i] && b[i].name));
}

/**
 * The load-time repair's write (14 Oct 2026): only if the stored labels are still the
 * ones this tab saw, read and written in one transaction. A tab that loaded during a
 * page change, or froze while taking in the server's writes, otherwise overwrote the
 * finished labels with its stale picture of them.
 * @returns {Promise<boolean>} true if written
 */
export async function writeLabelsIfUnchanged({ db, projectId, expected, labels }) {
    if (!projectId) {
        return false;
    }
    const ref = db.collection('projects').doc(projectId);
    return db.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists || !sameLabelNames(snap.data().pdfPageNumbers, expected)) {
            return false;
        }
        tx.update(ref, { pdfPageNumbers: labels });
        return true;
    });
}


// WEBPACK FOOTER //
// ./src/lib/page-labels.js